import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { SparkGlossaryService } from '../../services/spark-glossary.service';

/**
 * Right-hand glossary sheet, opened from the nav button or any
 * "What does this mean?" link.
 *
 * Built on a native `<dialog>` so focus trapping, `Esc` to close and the backdrop
 * come from the platform rather than being reimplemented.
 */
@Component({
  selector: 'sa-glossary-drawer',
  imports: [],
  templateUrl: './glossary-drawer.html',
  styleUrl: './glossary-drawer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlossaryDrawer {
  protected readonly glossaryService = inject(SparkGlossaryService);

  private readonly dialogRef = viewChild<ElementRef<HTMLDialogElement>>('glossaryDialog');

  constructor() {
    effect(() => {
      const dialog = this.dialogRef()?.nativeElement;
      if (!dialog) {
        return;
      }

      if (this.glossaryService.isOpen()) {
        if (!dialog.open) {
          dialog.showModal();
        }
      } else if (dialog.open) {
        dialog.close();
      }
    });
  }

  /** Keeps the service in step when the platform closes the dialog (Esc, or a form). */
  protected onDialogClose(): void {
    this.glossaryService.close();
  }

  protected close(): void {
    this.glossaryService.close();
  }

  /**
   * A native dialog's backdrop is part of the dialog element, so a click landing on
   * the element itself (rather than the panel inside it) is a backdrop click.
   */
  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === this.dialogRef()?.nativeElement) {
      this.close();
    }
  }
}
